import {
  PartnerOnboardingApplication,
  PartnerOnboardingEntityRequest,
  PartnerOnboardingLinkStatus,
  parseUTCDateTime,
} from 'vopay';
import { addDays, differenceInCalendarDays, format, isValid, parse } from 'date-fns';
import { fr } from 'date-fns/locale';

/** The identity card's expiry and link blocks: only shown when the entity has an onboarding application. */
export type ApplicationLink = {
  linkStatus: PartnerOnboardingLinkStatus;
  /** Local midnight of the expiry day. */
  expiryDate: Date;
  sentDate: Date;
  lastReminderSentDate: Date | null;
  extendDays: number;
  /**
   * The OTP-gated link, or `null` when the backend has none to give (e.g. one it withholds because it would
   * skip OTP, backend decision pending). The tiles still show; copy and open are disabled.
   */
  onboardingURL: string | null;
};

export function isSameEntity(a: PartnerOnboardingEntityRequest | null, b: PartnerOnboardingEntityRequest | null) {
  return a !== null && b !== null && a.EntityType === b.EntityType && a.EntityID === b.EntityID;
}

/**
 * `ExpiryDate` is a plain calendar day (`YYYY-MM-DD`), so it is read as that day in local time rather than
 * shifted from UTC: an applicant told "expires Sep 14" should see Sep 14 whatever the viewer's zone.
 */
function parseExpiryDate(value: string): Date | null {
  const parsed = parse(value, 'yyyy-MM-dd', new Date());

  return isValid(parsed) ? parsed : null;
}

/**
 * Narrows the application fields to the set the expiry and link blocks need. The backend sets them all
 * together, or nulls them all when there's no application (spec: "Null when the entity is not in
 * Application Pending or has no onboarding application"), so any one missing means "no application" and
 * the drawer shows only the identity card. `OnboardingURL` is the exception: it can be null with an
 * application, so it doesn't decide whether the blocks show.
 */
export function getApplicationLink(application: PartnerOnboardingApplication): ApplicationLink | null {
  const { LinkStatus, ExpiryDate, SentDate, LastReminderSentDate, ExtendDays, OnboardingURL } = application;

  if (LinkStatus === null || ExpiryDate === null || SentDate === null || ExtendDays === null) {
    return null;
  }

  const expiryDate = parseExpiryDate(ExpiryDate);
  const sentDate = parseUTCDateTime(SentDate);

  if (!expiryDate || !sentDate) return null;

  return {
    linkStatus: LinkStatus,
    expiryDate,
    sentDate,
    lastReminderSentDate: LastReminderSentDate ? parseUTCDateTime(LastReminderSentDate) : null,
    extendDays: ExtendDays,
    onboardingURL: OnboardingURL,
  };
}

/**
 * Whole days from today to the expiry day; negative once it has passed. Judgement call (spec: `ExpiryDate`
 * is a UTC day): "today" is the UTC day too, so near midnight the count matches when the backend actually
 * expires the link instead of being a day off for viewers west or east of UTC. No backend change needed.
 */
export function getDaysLeft(expiryDate: Date, now: Date = new Date()) {
  const utcToday = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  return differenceInCalendarDays(expiryDate, utcToday);
}

/**
 * The expiry Extend will set, for the confirm dialogue, by the backend's rule: an active link gets
 * `ExtendDays` added to its expiry day; an expired one gets `ExtendDays` from now, and the new
 * `ExpiryDate` is that instant's UTC day (so it can differ from the viewer's local "today + n").
 */
export function getExtendedExpiryDate({ linkStatus, expiryDate, extendDays }: ApplicationLink, now: Date = new Date()) {
  if (linkStatus !== 'Expired') return addDays(expiryDate, extendDays);

  const DAY_MS = 24 * 60 * 60 * 1000;
  const newExpiry = new Date(now.getTime() + extendDays * DAY_MS);

  return new Date(newExpiry.getUTCFullYear(), newExpiry.getUTCMonth(), newExpiry.getUTCDate());
}

/**
 * Figma's dates: "Sep 14, 2026" and, for "Sent on" and the Extend dialogue, the ordinal "Aug 15th, 2026".
 * French puts the day first ("14 sept. 2026", "1er oct. 2026").
 */
export function formatDisplayDate(date: Date, language: string, { isOrdinal = false } = {}) {
  if (language.startsWith('fr')) return format(date, isOrdinal ? 'do MMM yyyy' : 'd MMM yyyy', { locale: fr });

  return format(date, isOrdinal ? 'MMM do, yyyy' : 'MMM d, yyyy');
}
